/* eslint-disable max-lines */
import { useMemo, FC, useState, useEffect } from 'react';
import styled from '@emotion/styled';
import P1 from '@base/P1/P1';
import H1 from '@base/H1/H1';
import H4 from '@base/H4/H4';
import H6 from '@base/H6/H6';
import Divider from '@base/Divider/Divider';
import Image from '@base/Image';
import textHelper, { commasIndianFormat, pluralize } from '@utils/textHelper';
import { useRouter } from 'next/router';
import { getDateMonthString, getYear } from '@utils/dates';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { Spacings, mediaQuery, Fonts } from '@styles/index';
import {
  isCentreSubscription,
  subscriptionSubTypes,
  subscriptionTypes,
  subscriptionTypesObj
} from '@constants/index';
import { isEmpty } from 'lodash';
import { NeetUmeed, NeetUmeedTitle } from '@utils/constants';
import { IDurationInfoProps } from 'types/checkout';
import pushRoute from '@utils/pushRoute';
import { storeSubscriptionTypes } from '@cont/StoreListing/constants';
import useWindowSize from '@hooks/useWindowSize';
import useCreditsControl from '@api/hooks/credits/useCreditsControl';

import Typography from 'aquilla/core/atoms/Typography';
import {
  getComboGoalsByTier,
  getMwebBundleSummaryItems,
  getPlanTypeTagImage,
  getPlanTagDimensions
} from './bundleAddOnsHelper';

import {
  STORESUBSCRIPTION_PROCESSOR,
  STOREBUNDLE_PROCESSOR,
  STORE_PROCESSOR
} from './constants/index';
import SubscriptionAmountInfo from './SubscriptionAmountInfo';

const Desktop = styled.div`
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const Mobile = styled.div`
  ${mediaQuery.aboveMobile} {
    display: none;
  }
  width: 100%;
`;

const InfoContainer = styled.div`
  background-color: var(--color-base-1);
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_6B};
  margin-bottom: ${Spacings.SPACING_3B};
  box-shadow: ${boxShadowGenerator()};
  ${mediaQuery.uptoMobile} {
    margin-bottom: 0px;
    padding: ${Spacings.SPACING_4B};
  }
`;

const Duration = styled(P1)<{ $bundleDetails: boolean }>`
  margin-top: ${({ $bundleDetails }) =>
    $bundleDetails ? Spacings.SPACING_4B : Spacings.SPACING_1B};
`;

const DurationInformation = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${Spacings.SPACING_2B};

  ${mediaQuery.uptoMobile} {
    margin-top: 0;
  }
`;

const SubscriptionAmountInfoWrapper = styled.div<{
  $showFullBreakdown: boolean;
}>`
  display: ${({ $showFullBreakdown }) => !$showFullBreakdown && 'none'};
`;

const ChangeDurationWrapper = styled.div`
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_3B};
  }
`;

const DurTag = styled(P1)<{
  $bundleDetails?: boolean;
  $mwebCheckoutV2?: boolean;
}>`
  ${mediaQuery.uptoTablet} {
    flex: 1;
  }
  margin-top: ${({ $bundleDetails }) =>
    $bundleDetails ? Spacings.SPACING_4B : Spacings.SPACING_0B};
  white-space: ${({ $mwebCheckoutV2 }) =>
    $mwebCheckoutV2 ? 'nowrap' : 'unset'};
`;

const ChangeDuration = styled(H6)`
  cursor: pointer;
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H6};
    font-weight: ${Fonts.SEMIBOLD};
  }
`;

const BundleCardHeadWrap = styled.div`
  display: flex;
  justify-content: center;
  ${mediaQuery.uptoMobile} {
    justify-content: flex-start;
  }
`;

const BundleCardHeader = styled(H4)`
  margin-left: ${Spacings.SPACING_2B};
  line-height: 130%;
`;

const ImageWrapper = styled.div`
  position: relative;
`;

const StyledDivider = styled(Divider)`
  height: 1px;
  margin: ${Spacings.SPACING_4B} 0 ${Spacings.SPACING_4B} 0;
`;

const BundleCardContentWrap = styled.div`
  display: flex;
  margin-bottom: ${Spacings.SPACING_2B};
`;

const BundleCardContentText = styled(BundleCardHeader)`
  margin-left: ${Spacings.SPACING_3B};
  flex: 1;
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${Spacings.SPACING_3B};
`;

const InfoImageWrapper = styled.div<{ $mwebCheckoutV2: boolean }>`
  min-height: 74px;
  min-width: ${Spacings.SPACING_14B};
  position: relative;
  & > span > img {
    border-radius: ${Spacings.SPACING_1B};
    object-fit: contain;
  }
  ${mediaQuery.uptoMobile} {
    min-width: ${Spacings.SPACING_16B};
    min-height: 86px;
    ${({ $mwebCheckoutV2 }) => {
      if ($mwebCheckoutV2)
        return `
          min-width: 40px;
          min-height: 54px;
        `;
    }}
  }
`;

const InfoTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_1B};
`;

const RowWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 100%;
`;

const BundleRowWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Amount = styled.div`
  font-size: ${Fonts.H6};
  font-weight: ${Fonts.LIGHT};
  color: var(--color-text-primary);
  min-width: max-content;
`;

const Title = styled(H1)`
  font-size: ${Fonts.H4};
  font-weight: ${Fonts.HEAVY};
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H6};
    font-weight: ${Fonts.SEMIBOLD};
  }
`;

const DurTagChangeWrapper = styled.div<{ $mwebCheckoutV2: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  ${({ $mwebCheckoutV2 }) => {
    if (!$mwebCheckoutV2)
      return `
          gap: 65px;
          align-items: end;
        `;
    if ($mwebCheckoutV2)
      return `
          flex-wrap: wrap;
    `;
  }}
`;

const ComboDiscountLabel = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: var(--color-text-primary);
`;

const ComboDiscountAmount = styled(ComboDiscountLabel)`
  color: var(--color-i-green);
`;

const SubscriptionBreakdownWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const SubscriptionBreakdownLeftWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_1B};
`;

const SubscriptionBreakdownRightWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_1B};
  align-items: end;
`;

const StyP3 = styled(Typography)`
  text-decoration: underline;
`;

const getDurationText = (duration) => {
  if (duration < 30) return pluralize(duration, 'day');
  return pluralize(Math.floor(duration / 30), 'month');
};

const daysToMonth = (days) => Math.floor(days / 30);

const getValidDate = (validTillDate) => {
  return +validTillDate
    ? `Valid till ${
        validTillDate && getDateMonthString(validTillDate, false, false)
      }, ${validTillDate && getYear(validTillDate)}`
    : null;
};

const getValidFor = (monthsText) => {
  return `Valid for ${monthsText}`;
};

const getTotalDurationText = (subsDuration, extraDuration) => {
  if (subsDuration >= 30 && extraDuration >= 30) {
    return `${daysToMonth(subsDuration)} + ${daysToMonth(
      extraDuration
    )} months`;
  }
  if (subsDuration < 30 && extraDuration < 30) {
    return `${subsDuration} + ${extraDuration} days`;
  }
  if (subsDuration >= 30 && extraDuration < 30) {
    const subsDurationInMonths = daysToMonth(subsDuration);
    return `${subsDurationInMonths} month${
      subsDurationInMonths > 1 ? 's' : ''
    } + ${extraDuration} day${extraDuration > 1 ? 's' : ''}`;
  }
  const extraDurationInMonths = daysToMonth(extraDuration);
  return `${subsDuration} day${
    subsDuration > 1 ? 's' : ''
  } + ${extraDurationInMonths} month${extraDurationInMonths > 1 ? 's' : ''}`;
};

const DurationInfo: FC<IDurationInfoProps> = ({
  changeDuration = () => {},
  insideCurtain = false,
  isCombo = false,
  subscription,
  partPaymentStatus = null,
  cashResponse = null,
  bundleDetails = null,
  goal,
  extraDuration = 0,
  creditsApplied,
  discountedReferralSubscription,
  credits,
  isReferralValid,
  referralCode,
  setLoanInterestAmt,
  isLoanSelected,
  loanMeta,
  referralDiscount,
  mwebCheckoutV2,
  isMandate
}) => {
  const router = useRouter();
  const { includeSub, referral_code } = router.query;
  const { isMobile } = useWindowSize();
  const isNeetUmeed = includeSub && goal.uid === 'YOTUH' ? NeetUmeed : '';
  const isUaIconGoal = goal?.isUaIconGoal || false;
  const [showFullBreakdown, setShowFullBreakdown] = useState<boolean>(false);
  const [finalSubscriptionAmount, setFinalSubscriptionAmount] =
    useState<number>();

  const subsDuration = subscription?.duration || 0;
  const totalDurationTxt = extraDuration
    ? getTotalDurationText(subsDuration, extraDuration)
    : getDurationText(subsDuration);

  const validTillDate = new Date(
    subscription?.checkoutDatesWithBenefits?.endsAt
  );

  const externalAppText = subscription?.subTitle
    ? `(${subscription.subTitle})`
    : '';

  const { iconicGoals, iconicProGoals, plusGoals } = useMemo(
    () => getComboGoalsByTier(bundleDetails?.topologies),
    [bundleDetails]
  );

  const isPlatform = subscription?.type === subscriptionTypes.PLATFORM;

  const isStore = subscription?.processorName === STORE_PROCESSOR;
  const isStoreSubscriptionBased =
    subscription?.processorName === STORESUBSCRIPTION_PROCESSOR;

  useEffect(() => {
    if (!isCombo && referral_code) setShowFullBreakdown(true);
  }, [isCombo, referral_code]);

  // Iconic Pro CMS combo has topologies but no childSubscriptions — show goal rows by default
  useEffect(() => {
    if (
      bundleDetails?.topologies?.length &&
      !subscription?.childSubscriptions?.length
    ) {
      setShowFullBreakdown(true);
    }
  }, [bundleDetails, subscription?.childSubscriptions?.length]);

  const storeContentType = subscription?.contentTypeId;
  const isStoreBundle = subscription?.processorName === STOREBUNDLE_PROCESSOR;
  const isCentreComboSubscription = isCentreSubscription(
    subscription?.type,
    storeContentType
  );
  const storeContentUID = subscription?.objectUid;

  const accessEndDate = subscription?.accessEndDate;

  const isDurationBased = !!(!accessEndDate && subsDuration);

  let title = '';
  if (isStore) {
    title = subscription.title;
  } else {
    title = `${goal?.name} ${
      mwebCheckoutV2
        ? `· ${subscriptionTypesObj[subscription?.type]}`
        : 'subscription'
    }`;
  }
  if (
    subscription?.type === subscriptionTypes.ICONIC ||
    subscription?.type === subscriptionTypes.LITE
  ) {
    title = `${subscription?.title} ${
      isMobile && mwebCheckoutV2 ? '' : 'subscription'
    }`;
  } else if (isPlatform) {
    title = subscription?.value?.title;
  } else if (
    subscription?.type === subscriptionTypes.CENTRE ||
    isCentreSubscription(subscription?.type, storeContentType)
  ) {
    title = subscription.title;
  }

  const viewDetailRedirect = () => {
    let skuType = '';
    if (storeContentType === storeSubscriptionTypes.TEST_SERIES) {
      skuType = 'test-series';
    } else if (storeContentType === storeSubscriptionTypes.BATCH) {
      skuType = 'batch';
    } else if (storeContentType === storeSubscriptionTypes.NOTES) {
      skuType = 'notes';
    }

    const path = `/store/${skuType}/-/${storeContentUID}`;
    pushRoute(path, path);
  };

  const getStoreImage = () => {
    if (storeContentType === storeSubscriptionTypes.TEST_SERIES) {
      return 'store/checkout/test-series-mweb.svg';
    }
    if (storeContentType === storeSubscriptionTypes.BATCH) {
      return 'store/checkout/batch-mweb.svg';
    }
    if (storeContentType === storeSubscriptionTypes.NOTES) {
      return 'store/checkout/notes.svg';
    }
  };
  const showChangeDuration = useMemo(() => {
    return (
      !insideCurtain &&
      !isPlatform &&
      !partPaymentStatus?.is_active_part_set &&
      !cashResponse &&
      !isUaIconGoal &&
      !isStore
    );
  }, [
    insideCurtain,
    isPlatform,
    partPaymentStatus?.is_active_part_set,
    cashResponse,
    isUaIconGoal,
    isStore
  ]);

  // TODO: Move this logic at common place, copied from SubscriptionAmountInfo

  const { isCreditsEnabledOnPlatform } = useCreditsControl();
  const subscriptionValue =
    (cashResponse ? cashResponse.subscriptionDetails : subscription) || {};

  const creditsAppliedFlag =
    (creditsApplied || cashResponse?.credits > 0) && isCreditsEnabledOnPlatform;

  const taxBreakdown = creditsAppliedFlag
    ? subscriptionValue.taxBreakdownWithCredits
    : subscriptionValue.taxBreakdown;

  const total =
    (partPaymentStatus?.isActivePartSet &&
      partPaymentStatus?.partPayments?.reduce((a, b) => {
        return {
          amount: a.amount + b.amount
        };
      }).amount) ||
    Math.round(taxBreakdown?.find((item) => item.label === 'Total')?.amount);
  const currency = textHelper.currencySymbolMap(subscriptionValue.currency);

  const subscriptionFee =
    subscriptionValue?.maxRetailPrice || Math.round(subscriptionValue.price);
  const subscriptionFeeText = `${currency}${textHelper.commas(
    subscriptionFee
  )}`;

  const totalAmount = `${currency}${textHelper.commas(total)}`;

  const getTotalAmountForBundle = (taxBreakdown) => {
    const total = Math.round(
      taxBreakdown?.find((item) => item.label === 'Total')?.amount
    );
    const totalAmount = `${currency}${textHelper.commas(total)}`;
    return totalAmount;
  };

  const renderReferralDiscount = () => {
    if (isReferralValid && referralCode && referralDiscount > 0) {
      return (
        <RowWrapper>
          <ComboDiscountLabel>{`${referralCode.toUpperCase()} discount`}</ComboDiscountLabel>
          <ComboDiscountAmount>
            {`- ${currency}${textHelper.commas(Math.round(referralDiscount))}`}
          </ComboDiscountAmount>
        </RowWrapper>
      );
    }
  };

  const getBundleComponent = () => {
    const { displayDuration = '', childSubscriptions = [] } =
      subscription ?? {};
    const mwebLineItems = getMwebBundleSummaryItems({
      subscription,
      topologies: bundleDetails?.topologies,
      fallbackDuration: totalDurationTxt
    });
    const childSubscriptionsLength =
      mwebLineItems.length || childSubscriptions?.length;
    const firstSubscriptionTitle =
      mwebLineItems[0]?.title ||
      (childSubscriptions[0] && childSubscriptions[0]?.title);
    if (!isMobile || !mwebCheckoutV2)
      return (
        <>
          <BundleCardHeadWrap>
            <ImageWrapper>
              <Image width={16} height={16} src="bundles/stars.png" alt="-" />
            </ImageWrapper>
            <BundleCardHeader>Combo subscription</BundleCardHeader>
          </BundleCardHeadWrap>
          <StyledDivider />
          {plusGoals && (
            <BundleCardContentWrap>
              <ImageWrapper>
                <Image
                  width={getPlanTagDimensions(isMobile).width}
                  height={getPlanTagDimensions(isMobile).height}
                  src={getPlanTypeTagImage(subscriptionTypes.PLUS)}
                  alt="-"
                />
              </ImageWrapper>
              <BundleCardContentText>{plusGoals}</BundleCardContentText>
            </BundleCardContentWrap>
          )}
          {iconicGoals && (
            <BundleCardContentWrap>
              <ImageWrapper>
                <Image
                  width={getPlanTagDimensions(isMobile).width}
                  height={getPlanTagDimensions(isMobile).height}
                  src={getPlanTypeTagImage(subscriptionTypes.ICONIC)}
                  alt="-"
                />
              </ImageWrapper>
              <BundleCardContentText>{iconicGoals}</BundleCardContentText>
            </BundleCardContentWrap>
          )}
          {iconicProGoals && (
            <BundleCardContentWrap>
              <ImageWrapper>
                <Image
                  width={getPlanTagDimensions(isMobile).width}
                  height={getPlanTagDimensions(isMobile).height}
                  src={getPlanTypeTagImage(subscriptionTypes.ICONIC_PRO)}
                  alt="-"
                />
              </ImageWrapper>
              <BundleCardContentText>{iconicProGoals}</BundleCardContentText>
            </BundleCardContentWrap>
          )}
        </>
      );

    return (
      <>
        {showFullBreakdown ? (
          <>
            <BundleRowWrapper>
              {mwebLineItems.map((item) => (
                <RowWrapper key={item.key}>
                  <InfoTextWrapper>
                    <Title
                      role="subscriptionInfo-title"
                      color="var(--color-i-black)"
                    >
                      {item.title}
                    </Title>
                    <DurTagChangeWrapper $mwebCheckoutV2={mwebCheckoutV2}>
                      <DurTag
                        role="subscriptionInfo-duration"
                        color="var(--color-text-secondary)"
                      >
                        {item.displayDuration}
                      </DurTag>
                      {item.endsAt ? (
                        <DurTag
                          color="var(--color-text-secondary)"
                          role="subscriptionInfo-validity"
                          $mwebCheckoutV2={mwebCheckoutV2}
                        >
                          &nbsp;{' · '}
                          {getValidDate(new Date(item.endsAt))}
                        </DurTag>
                      ) : null}
                    </DurTagChangeWrapper>
                  </InfoTextWrapper>
                  {item.showPrice ? (
                    <Amount>
                      {getTotalAmountForBundle(item.taxBreakdown)}
                    </Amount>
                  ) : null}
                </RowWrapper>
              ))}
            </BundleRowWrapper>
            {subscription?.bundleSavedAmount > 0 ? (
              <>
                <StyledDivider />
                <RowWrapper>
                  <ComboDiscountLabel>Combo discount</ComboDiscountLabel>
                  <ComboDiscountAmount>
                    {`- ${currency}${textHelper.commas(
                      Math.round(subscription.bundleSavedAmount)
                    )}`}
                  </ComboDiscountAmount>
                </RowWrapper>
              </>
            ) : null}
          </>
        ) : (
          <SubscriptionBreakdownWrapper>
            <SubscriptionBreakdownLeftWrapper>
              <Typography variant="h5">
                {firstSubscriptionTitle}
                {childSubscriptionsLength > 1 &&
                  `+ ${childSubscriptionsLength - 1} more`}
              </Typography>
              <Typography variant="p3" color="var(--color-i-gray-13)">
                {displayDuration || totalDurationTxt}
              </Typography>
            </SubscriptionBreakdownLeftWrapper>
            <SubscriptionBreakdownRightWrapper
              onClick={() => setShowFullBreakdown(true)}
            >
              <Typography variant="h5">
                ₹{commasIndianFormat(finalSubscriptionAmount)}
              </Typography>
              <StyP3 variant="p3">See full breakdown</StyP3>
            </SubscriptionBreakdownRightWrapper>
          </SubscriptionBreakdownWrapper>
        )}
      </>
    );
  };
  return (
    <InfoContainer role="subscriptionInfo-planInfoContainer">
      {bundleDetails ? (
        getBundleComponent()
      ) : (
        <InfoWrapper>
          {(isStoreSubscriptionBased || isStoreBundle) && (
            <InfoImageWrapper $mwebCheckoutV2={mwebCheckoutV2}>
              <Image
                src={subscription?.value?.thumbnail || getStoreImage()}
                layout="fill"
                alt="avatar"
              />
            </InfoImageWrapper>
          )}
          {mwebCheckoutV2 ? (
            <Mobile>
              <RowWrapper>
                <InfoTextWrapper>
                  <Title
                    role="subscriptionInfo-title"
                    color="var(--color-i-black)"
                  >
                    {isNeetUmeed ? NeetUmeedTitle : title}
                  </Title>
                  <DurTagChangeWrapper $mwebCheckoutV2={mwebCheckoutV2}>
                    {!isPlatform &&
                      !isStore &&
                      !isStoreSubscriptionBased &&
                      !isStoreBundle && (
                        <DurTag
                          $bundleDetails={!!bundleDetails}
                          role="subscriptionInfo-duration"
                          color="var(--color-text-secondary)"
                          $mwebCheckoutV2={mwebCheckoutV2}
                        >
                          {`${totalDurationTxt} ${externalAppText}`}
                        </DurTag>
                      )}
                    {!isCombo && !bundleDetails && !isUaIconGoal && (
                      <DurTag
                        color="var(--color-text-secondary)"
                        role="subscriptionInfo-validity"
                        $mwebCheckoutV2={mwebCheckoutV2}
                      >
                        {isStoreSubscriptionBased || isStoreBundle
                          ? null
                          : '\u00A0·\u00A0'}

                        {isStoreBundle
                          ? getValidFor(totalDurationTxt)
                          : getValidDate(validTillDate)}
                      </DurTag>
                    )}
                  </DurTagChangeWrapper>
                </InfoTextWrapper>
                <Amount>{subscriptionFeeText}</Amount>
              </RowWrapper>
            </Mobile>
          ) : (
            <Mobile>
              <InfoTextWrapper>
                <Title
                  role="subscriptionInfo-title"
                  color="var(--color-i-black)"
                >
                  {isNeetUmeed ? NeetUmeedTitle : title}
                </Title>
                {!isPlatform &&
                  !isStore &&
                  !isStoreSubscriptionBased &&
                  !isStoreBundle && (
                    <Duration
                      $bundleDetails={!!bundleDetails}
                      role="subscriptionInfo-duration"
                    >
                      {`${totalDurationTxt} ${externalAppText}`}
                    </Duration>
                  )}
                <DurTagChangeWrapper $mwebCheckoutV2={mwebCheckoutV2}>
                  {!isCombo && !bundleDetails && !isUaIconGoal && (
                    <DurTag
                      color="var(--color-text-secondary)"
                      role="subscriptionInfo-validity"
                    >
                      {isStoreBundle || isDurationBased
                        ? getValidFor(totalDurationTxt)
                        : getValidDate(validTillDate)}
                    </DurTag>
                  )}
                  {showChangeDuration &&
                    !isStoreSubscriptionBased &&
                    !isStoreBundle && (
                      <ChangeDuration
                        color="var(--color-i-green)"
                        onClick={changeDuration}
                      >
                        Change duration
                      </ChangeDuration>
                    )}
                </DurTagChangeWrapper>
              </InfoTextWrapper>
            </Mobile>
          )}

          <Desktop>
            <Title role="subscriptionInfo-title" color="var(--color-i-black)">
              {isNeetUmeed ? NeetUmeedTitle : title}
            </Title>
          </Desktop>
        </InfoWrapper>
      )}
      {!isPlatform &&
        !isStore &&
        !isStoreSubscriptionBased &&
        !isStoreBundle && (
          <Desktop>
            <Duration
              $bundleDetails={!!bundleDetails}
              role="subscriptionInfo-duration"
            >
              {`${totalDurationTxt} ${externalAppText}`}
            </Duration>
          </Desktop>
        )}
      <DurationInformation>
        {!isCombo && !bundleDetails && !isUaIconGoal && (
          <Desktop>
            <DurTag
              color="var(--color-text-secondary)"
              role="subscriptionInfo-validity"
            >
              {isStoreBundle || isDurationBased
                ? getValidFor(totalDurationTxt)
                : getValidDate(validTillDate)}
            </DurTag>
          </Desktop>
        )}
        {showChangeDuration && (
          <>
            <Desktop>
              {isMandate ? null : (
                <ChangeDuration
                  color="var(--color-i-green)"
                  onClick={
                    !isCentreComboSubscription &&
                    (isStoreSubscriptionBased || isStoreBundle)
                      ? viewDetailRedirect
                      : changeDuration
                  }
                >
                  {!isCentreComboSubscription &&
                  (isStoreSubscriptionBased || isStoreBundle)
                    ? 'View details'
                    : 'Change Duration'}
                </ChangeDuration>
              )}
            </Desktop>
            {!mwebCheckoutV2 && isStoreSubscriptionBased && (
              <Mobile>
                <ChangeDurationWrapper>
                  <ChangeDuration
                    color="var(--color-i-green)"
                    onClick={viewDetailRedirect}
                  >
                    View details
                  </ChangeDuration>
                </ChangeDurationWrapper>
              </Mobile>
            )}
          </>
        )}
      </DurationInformation>
      {isMobile && mwebCheckoutV2 && (
        <SubscriptionAmountInfoWrapper
          $showFullBreakdown={
            (!isEmpty(subscriptionValue?.subscriptionPriceBreakdown) &&
              subscriptionValue?.subscriptionPriceBreakdown !== null) ||
            showFullBreakdown
          }
        >
          <SubscriptionAmountInfo
            subscription={discountedReferralSubscription}
            creditsApplied={creditsApplied}
            credits={credits}
            isReferralValid={isReferralValid}
            referralDiscount={referralDiscount}
            referralCode={referralCode}
            partPaymentStatus={partPaymentStatus}
            cashResponse={cashResponse}
            setLoanInterestAmt={setLoanInterestAmt}
            isLoanSelected={isLoanSelected}
            loanMeta={loanMeta}
            mwebCheckoutV2={mwebCheckoutV2}
            setFinalSubscriptionAmount={setFinalSubscriptionAmount}
            showFullBreakDown={showFullBreakdown}
          />
        </SubscriptionAmountInfoWrapper>
      )}
    </InfoContainer>
  );
};

export default DurationInfo;
